'use client';

import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ADMIN_ROUTES } from '@/config/router';
import { useSuperAdmin } from '@/hooks/use-superadmin';
import { trpc } from '@/lib/api/trpc';
import {
  StripePricingGroupPicker,
  isStripePricingPickerValid,
} from '@/components/partners/StripePricingGroupPicker';
import { DomainProvisioning } from '@/components/partners/DomainProvisioning';
import { PartnerPublish } from '@/components/partners/PartnerPublish';

/**
 * Edit partner — superadmin form complementing `/admin/partners/new`.
 *
 * Why a dedicated edit page (vs inline cells on the list):
 *   - Pricing group is one of ~7 editable knobs; inline editing each
 *     would clutter the triage table.
 *   - We need read-then-write semantics (load current snapshot, mutate
 *     only the changed fields) which is awkward to express per-row.
 *   - Concentrating writes in one place keeps the use-case wiring
 *     consistent: every field below maps 1:1 to a column on the
 *     `partners` table and goes through `updatePartner` in
 *     `useCases/iam/partners` (same path Hub admin uses).
 *
 * Field scope intentionally mirrors what's editable at creation time
 * in `/admin/partners/new` plus the lifecycle knobs (`status`,
 * `subscribedProducts`). Rich fields that have their own dedicated
 * UIs elsewhere (`billingDeal`, `branding`, `commercialTerms`,
 * `revenueSharePolicy`, `applicationPreferences`) are intentionally
 * NOT mirrored here — that would duplicate the Hub-side editors and
 * invite drift. The "Status / Pricing / Domain" cluster is what AMs
 * actually need from CRM admin.
 *
 * The pricing-group picker (Standard / BNP / Custom) is identical
 * to the `/new` wizard so AMs see one consistent control.
 */
export default function EditPartnerPage() {
  const navigate = useNavigate();
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam ?? '';
  const { isSuperAdmin, isLoading: loadingRole } = useSuperAdmin();

  const partnerQuery = trpc.partners.getBySlug.useQuery(
    { slug },
    { enabled: isSuperAdmin && slug.length > 0, staleTime: 30_000 },
  );

  const utils = trpc.useUtils();
  const updateMutation = trpc.partners.update.useMutation({
    onSuccess: (result) => {
      toast.success(`Partner "${result.slug}" updated`);
      // Refresh the per-partner snapshot + the macro list — both
      // reflect any of the fields below.
      utils.partners.getBySlug.invalidate({ slug });
      utils.partners.listWithOrgs.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  if (loadingRole) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            Editing partners is restricted to superadmin users.
          </p>
        </div>
      </div>
    );
  }

  if (partnerQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading partner…</div>;
  }

  if (partnerQuery.error || !partnerQuery.data) {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to={ADMIN_ROUTES.PARTNERS}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to partners
          </Link>
        </Button>
        <Alert variant="destructive">
          <AlertTitle>Could not load partner</AlertTitle>
          <AlertDescription>
            {partnerQuery.error?.message ?? `No partner with slug "${slug}".`}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <EditPartnerForm
      partner={partnerQuery.data}
      onSave={(payload) => updateMutation.mutate(payload)}
      isSaving={updateMutation.isPending}
      lastError={updateMutation.error?.message}
      lastSavedAt={updateMutation.data ? new Date() : null}
      onBack={() => navigate(ADMIN_ROUTES.PARTNERS)}
    />
  );
}

// ---------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------

// Pull the exact server-side types so the form mirrors the use-case
// surface byte-for-byte — adding a column on `partners.update` will
// surface here as a compiler error if we forget to wire it up.
type PartnerDetail = inferRouterOutputs<AppRouter>['partners']['getBySlug'];
type UpdateInput = inferRouterInputs<AppRouter>['partners']['update'];

// The hardcoded preset list ({ standard, bnp, custom }) was retired
// once the picker started discovering groups dynamically — see
// `StripePricingGroupPicker` for the new selection model.

function EditPartnerForm({
  partner,
  onSave,
  isSaving,
  lastError,
  lastSavedAt,
  onBack,
}: {
  partner: PartnerDetail;
  onSave: (payload: UpdateInput) => void;
  isSaving: boolean;
  lastError: string | undefined;
  lastSavedAt: Date | null;
  onBack: () => void;
}) {
  // Form state — initialised from the loaded snapshot. We only send
  // the fields that actually changed (compared to `partner.*`) so the
  // server-side jsonb-merge stays surgical.
  const [name, setName] = React.useState(partner.name);
  const [supportEmail, setSupportEmail] = React.useState(partner.supportEmail ?? '');
  const [billingEmail, setBillingEmail] = React.useState(partner.billingEmail ?? '');
  const [slackChannelId, setSlackChannelId] = React.useState(partner.slackChannelId ?? '');
  const [status, setStatus] = React.useState<PartnerDetail['status']>(partner.status);
  const [subscribedProducts, setSubscribedProducts] = React.useState<
    ('crm' | 'payfac')[]
  >(partner.subscribedProducts);
  const [mode, setMode] = React.useState<PartnerDetail['mode']>(partner.mode);

  const [rootDomain, setRootDomain] = React.useState(partner.partnerDomain?.rootDomain ?? '');
  // Operators provide ONLY the root domain + the sub-domain the app is
  // served from. The app host and Clerk FAPI host are derived from those
  // (app host = "<sub>.<root>", Clerk host = "clerk.<root>") and shown
  // read-only in the generated preview below — nobody has to guess the
  // exact strings. When editing an existing partner we reverse the stored
  // app host back into its sub-domain label so the form round-trips.
  const [subdomain, setSubdomain] = React.useState(
    deriveSubdomain(partner.partnerDomain?.appHost ?? '', partner.partnerDomain?.rootDomain ?? ''),
  );

  const trimmedRoot = rootDomain.trim().toLowerCase();
  const trimmedSub = subdomain.trim().toLowerCase();
  const appHost = trimmedRoot && trimmedSub ? `${trimmedSub}.${trimmedRoot}` : '';
  const clerkFapi = trimmedRoot ? `clerk.${trimmedRoot}` : '';

  // The picker emits `string` (pick a group), `null` (revert to
  // platform standard), or `undefined` (Custom branch in an invalid
  // state — disable submit). We track the raw value here and translate
  // it into the patch payload below.
  const [pricingGroupSelection, setPricingGroupSelection] = React.useState<
    string | null | undefined
  >(partner.stripePricingGroup);
  const pricingPickerValid = isStripePricingPickerValid(pricingGroupSelection);

  const domainComplete = trimmedRoot.length > 0 && trimmedSub.length > 0;

  // `mode='partner-domain'` requires a domain config — if the AM
  // clears it without flipping mode, surface that early.
  const domainModeMismatch = mode === 'partner-domain' && !domainComplete;

  // Build a minimal-patch payload. Only send fields the AM actually
  // touched relative to the current row. Sending unchanged values is
  // harmless (the use-case is idempotent), but it pollutes audit
  // logs and forces a jsonb merge for nothing.
  const buildPayload = (): UpdateInput => {
    const payload: UpdateInput = { id: partner.id };

    const trimmedName = name.trim();
    if (trimmedName !== partner.name) payload.name = trimmedName;

    if (status !== partner.status) payload.status = status;

    if (
      !sameProductSet(subscribedProducts, partner.subscribedProducts)
    ) {
      payload.subscribedProducts = subscribedProducts;
    }

    if (mode !== partner.mode) payload.mode = mode;

    const trimmedSupport = supportEmail.trim();
    const initialSupport = partner.supportEmail ?? '';
    if (trimmedSupport !== initialSupport) {
      payload.supportEmail = trimmedSupport === '' ? null : trimmedSupport;
    }

    const trimmedBilling = billingEmail.trim();
    const initialBilling = partner.billingEmail ?? '';
    if (trimmedBilling !== initialBilling) {
      payload.billingEmail = trimmedBilling === '' ? null : trimmedBilling;
    }

    const trimmedSlack = slackChannelId.trim().toUpperCase();
    const initialSlack = partner.slackChannelId ?? '';
    if (trimmedSlack !== initialSlack) {
      payload.slackChannelId = trimmedSlack === '' ? null : trimmedSlack;
    }

    const currentDomain = partner.partnerDomain;
    const newDomain = domainComplete
      ? { rootDomain: trimmedRoot, appHost, clerkFapi }
      : null;
    if (!sameDomain(currentDomain, newDomain)) {
      payload.partnerDomain = newDomain;
    }

    // Picker emits three intents:
    //   - `null`      → "revert to platform standard" (clears column),
    //   - `acctgrp_…` → explicit group ID,
    //   - `undefined` → Custom branch incomplete (caller already
    //                   disables submit via `pricingPickerValid`).
    if (pricingGroupSelection !== undefined) {
      const desired = pricingGroupSelection;
      if (desired !== (partner.stripePricingGroup ?? null)) {
        payload.stripePricingGroup = desired;
      }
    }

    return payload;
  };

  // Disable save when nothing changed OR validation is currently red.
  // We compute `dirty` from the same payload builder so the button
  // stays in sync with what would actually be sent.
  const previewPayload = buildPayload();
  const { id: _id, ...dirtyFields } = previewPayload;
  const dirty = Object.keys(dirtyFields).length > 0;

  // Mirror the server-side `partnerSlackChannelIdSchema` so a typo'd
  // channel id fails in the form, not in the tRPC error toast.
  const trimmedSlackForValidation = slackChannelId.trim().toUpperCase();
  const slackChannelValid =
    trimmedSlackForValidation === '' || /^[CG][A-Z0-9]{6,20}$/.test(trimmedSlackForValidation);

  const canSave =
    dirty &&
    !isSaving &&
    name.trim().length >= 2 &&
    subscribedProducts.length > 0 &&
    pricingPickerValid &&
    slackChannelValid &&
    !domainModeMismatch;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to partners
      </Button>

      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Edit partner
        </h1>
        <Badge variant="outline" className="font-mono text-xs">{partner.slug}</Badge>
        <Badge variant="secondary" className="text-xs">superadmin</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>
            The <code>slug</code> is immutable — it's referenced by{' '}
            <code>accounts.partner</code>, Clerk session claims, and the frontend
            mirror. Need to rename? Create a new partner row + reassign orgs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" value={partner.slug} disabled className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Internal name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={2}
                maxLength={120}
              />
              <p className="text-xs text-muted-foreground">
                Used internally by Tagada ops.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="supportEmail">Support email</Label>
              <Input
                id="supportEmail"
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                placeholder="support@partner.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Statement delivery</CardTitle>
          <CardDescription>
            Where the <strong>weekly billing statement</strong> is sent every
            Monday. Email falls back to the support email, then to the
            partner's active <code>partneradmin</code> accounts when empty —
            but an explicit billing contact is strongly preferred. Slack
            channel id (<code>C…</code>) is optional; when empty the Slack leg
            is skipped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="billingEmail">Billing email</Label>
              <Input
                id="billingEmail"
                type="email"
                value={billingEmail}
                onChange={(e) => setBillingEmail(e.target.value)}
                placeholder="billing@partner.com"
              />
              <p className="text-xs text-muted-foreground">
                Weekly statement recipient. Empty → support email →
                partneradmins.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slackChannelId">Slack channel ID</Label>
              <Input
                id="slackChannelId"
                value={slackChannelId}
                onChange={(e) => setSlackChannelId(e.target.value)}
                placeholder="C0XXXXXXXXX"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Shared channel the statement is posted to (usually{' '}
                <code>#partnership-tagada-&lt;slug&gt;</code>). Right-click the
                channel in Slack → View channel details → the ID is at the
                bottom.
              </p>
              {!slackChannelValid ? (
                <p className="text-xs text-destructive">
                  Must look like a Slack channel id: <code>C…</code> or{' '}
                  <code>G…</code> followed by 6–20 uppercase letters/digits.
                </p>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lifecycle</CardTitle>
          <CardDescription>
            Status flips between <code>active</code> and <code>paused</code> here.
            Offboarding is a separate, deliberate action — route through{' '}
            <code>archivePartner</code> from the Hub (we don't expose it from
            CRM admin so an AM can't accidentally tombstone a live partner).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex flex-col gap-2 rounded-md border p-3 text-sm">
                {(['active', 'paused'] as const).map((s) => (
                  <label key={s} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      checked={status === s}
                      onChange={() => setStatus(s)}
                    />
                    <span className="font-medium capitalize">{s}</span>
                    {partner.status === s ? (
                      <span className="text-xs text-muted-foreground">(current)</span>
                    ) : null}
                  </label>
                ))}
                {partner.status === 'offboarded' ? (
                  <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                    This partner is currently <strong>offboarded</strong>. Reactivating
                    here flips status to <code>active</code> but does NOT restore
                    archived staff / orgs — handle those in Hub admin.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subscribed products</Label>
              <div className="flex flex-col gap-2 rounded-md border p-3 text-sm">
                {(['crm', 'payfac'] as const).map((product) => (
                  <div key={product} className="flex items-center gap-2">
                    <Checkbox
                      id={`product-${product}`}
                      checked={subscribedProducts.includes(product)}
                      onCheckedChange={(checked) =>
                        setSubscribedProducts((prev) =>
                          checked
                            ? Array.from(new Set([...prev, product]))
                            : prev.filter((p) => p !== product),
                        )
                      }
                    />
                    <Label htmlFor={`product-${product}`} className="font-normal">
                      {product}
                    </Label>
                  </div>
                ))}
                {subscribedProducts.length === 0 ? (
                  <p className="text-xs text-destructive">
                    At least one product must be selected.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Domain / Mode</CardTitle>
          <CardDescription>
            <code>partner-domain</code> partners get their own Clerk satellite +
            host. Enter the <strong>root domain</strong> and the{' '}
            <strong>sub-domain</strong> the app runs on — the app host and Clerk
            FAPI host are generated for you below. <code>reseller-only</code>{' '}
            partners are tagged on Clerk orgs but live on the main domain.
            Clearing the root domain forces <code>reseller-only</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Mode</Label>
            <div className="flex gap-3 rounded-md border p-3 text-sm">
              {(['partner-domain', 'reseller-only'] as const).map((m) => (
                <label key={m} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === m}
                    onChange={() => setMode(m)}
                  />
                  <span className="font-medium">{m}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="rootDomain">Root domain</Label>
              <Input
                id="rootDomain"
                placeholder="acme-example.com"
                value={rootDomain}
                onChange={(e) => setRootDomain(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The partner's main domain — no <code>https://</code>, no
                sub-domain.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subdomain">App sub-domain</Label>
              <Input
                id="subdomain"
                placeholder="app"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Just the label the dashboard runs on (e.g. <code>app</code>), not
                the full host.
              </p>
            </div>
          </div>

          {/* App host + Clerk FAPI host are derived, never typed by hand. */}
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Generated automatically
            </p>
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">App host</dt>
                <dd className="font-mono">{appHost || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Clerk FAPI host</dt>
                <dd className="font-mono">{clerkFapi || '—'}</dd>
              </div>
            </dl>
          </div>

          {trimmedRoot.length >= 2 && trimmedSub.length >= 1 ? (
            <>
              <DomainProvisioning rootDomain={trimmedRoot} subdomain={trimmedSub} />
              <PartnerPublish
                brand={partner.slug}
                rootDomain={trimmedRoot}
                subdomain={trimmedSub}
                label={partner.name}
              />
            </>
          ) : null}

          {domainModeMismatch ? (
            <Alert variant="destructive">
              <AlertTitle>Domain required</AlertTitle>
              <AlertDescription>
                Mode is <code>partner-domain</code> but the domain is incomplete.
                Fill the root domain and the app sub-domain, or switch mode to{' '}
                <code>reseller-only</code>.
              </AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stripe pricing group</CardTitle>
          <CardDescription>
            Stripe Connect <code>payments_pricing</code> group applied when we
            mint a sub-account for one of this partner's merchants. Choosing{' '}
            <strong>Standard</strong> clears any custom override so the runtime
            resolver falls back to the platform default. The list below is
            discovered live from Stripe + the partners table.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StripePricingGroupPicker
            currentValue={partner.stripePricingGroup}
            onChange={setPricingGroupSelection}
            disabled={isSaving}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Save</CardTitle>
          <CardDescription>
            Only fields you actually changed will be sent.{' '}
            {dirty ? (
              <span className="font-medium">
                {Object.keys(dirtyFields).length} field
                {Object.keys(dirtyFields).length === 1 ? '' : 's'} pending.
              </span>
            ) : (
              <span>No changes yet.</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {lastSavedAt ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Saved</AlertTitle>
              <AlertDescription>
                Updated at {lastSavedAt.toLocaleTimeString()}.
              </AlertDescription>
            </Alert>
          ) : null}

          {lastError ? (
            <Alert variant="destructive">
              <AlertTitle>Save failed</AlertTitle>
              <AlertDescription>{lastError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center gap-3">
            <Button disabled={!canSave} onClick={() => onSave(buildPayload())}>
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button variant="outline" onClick={onBack}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers — compare current snapshot vs form state so we only emit
// fields the AM actually touched. Order-insensitive for the product
// set; deep-equal for the partner-domain triple.
// ---------------------------------------------------------------------

// Operators enter only the root domain + the app sub-domain; the app
// host and Clerk FAPI host are derived. When loading an existing partner
// we reverse the stored app host back into its sub-domain label so the
// form round-trips (e.g. "app.acme.com" + root "acme.com" → "app").
function deriveSubdomain(appHost: string, rootDomain: string): string {
  const host = appHost.trim().toLowerCase();
  const root = rootDomain.trim().toLowerCase();
  if (!host) return root ? 'app' : '';
  if (root && host.endsWith(`.${root}`)) {
    return host.slice(0, host.length - root.length - 1);
  }
  // Fallback for legacy/odd configs: use the first label.
  return host.split('.')[0] ?? '';
}

function sameProductSet(a: ('crm' | 'payfac')[], b: ('crm' | 'payfac')[]) {
  if (a.length !== b.length) return false;
  const as = new Set(a);
  for (const p of b) if (!as.has(p)) return false;
  return true;
}

function sameDomain(
  a: PartnerDetail['partnerDomain'],
  b: { rootDomain: string; appHost: string; clerkFapi: string } | null,
) {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return (
    a.rootDomain === b.rootDomain &&
    a.appHost === b.appHost &&
    a.clerkFapi === b.clerkFapi
  );
}
